import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Badge,
	Button,
	Form,
	Spinner,
	Table,
} from "react-bootstrap";
import Modal from "../../common/components/ThemedModal";
import { FaHistory } from "react-icons/fa";
import { Rider } from "../../../types/model";
import { getRiderHistory } from "../../../services/resultService";
import AthleteProfileDrawer from "../../Rider/AthleteProfileDrawer";
import {
	buildSeasonOptions,
	formatDateValue,
	getSeasonHistory,
	hasMeaningfulPhaseData,
	RiderHistoryItem,
	RiderHistoryPhase,
} from "../../Rider/athleteProfileUtils";

interface RiderHistoryButtonProps {
	riderId?: string;
	riderName: string;
	size?: "sm" | "lg";
	className?: string;
	iconOnly?: boolean;
	hideIcon?: boolean;
	initialRider?: Rider | null;
}

const formatPhaseValue = (phase?: RiderHistoryPhase) => {
	if (!hasMeaningfulPhaseData(phase)) {
		return <span className="text-muted">-</span>;
	}

	const rank = (phase?.rank ?? 0) > 0 ? `#${phase?.rank}` : "-";
	const points =
		phase?.points !== undefined && phase?.points !== null
			? `${phase.points} pts`
			: "-";

	return (
		<div className="d-flex flex-column">
			<span className="fw-semibold">{rank}</span>
			<span className="small text-muted">{points}</span>
		</div>
	);
};

const RiderHistoryButton = ({
	riderId,
	riderName,
	size = "sm",
	className = "",
	iconOnly = false,
	hideIcon = false,
	initialRider,
}: RiderHistoryButtonProps) => {
	const [showModal, setShowModal] = useState(false);
	const [showProfileDrawer, setShowProfileDrawer] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [history, setHistory] = useState<RiderHistoryItem[]>([]);
	const [selectedSeasonKey, setSelectedSeasonKey] = useState("");

	const seasonOptions = useMemo(() => buildSeasonOptions(history), [history]);

	useEffect(() => {
		if (seasonOptions.length === 0) {
			setSelectedSeasonKey("");
			return;
		}

		setSelectedSeasonKey((currentValue) => {
			if (seasonOptions.some((seasonOption) => seasonOption.key === currentValue)) {
				return currentValue;
			}
			return seasonOptions[0].key;
		});
	}, [seasonOptions]);

	const seasonHistory = useMemo(
		() => getSeasonHistory(history, selectedSeasonKey),
		[history, selectedSeasonKey],
	);

	const totalHistoryPoints = useMemo(
		() =>
			seasonHistory.reduce(
				(total, item) => total + (Number(item.total_points ?? 0) || 0),
				0,
			),
		[seasonHistory],
	);

	const showQualifyingColumn = useMemo(
		() =>
			seasonHistory.some((historyItem) =>
				hasMeaningfulPhaseData(historyItem.qualifying_data),
			),
		[seasonHistory],
	);

	const showSemisColumn = useMemo(
		() =>
			seasonHistory.some((historyItem) =>
				hasMeaningfulPhaseData(historyItem.semis_data),
			),
		[seasonHistory],
	);

	const openHistory = async () => {
		if (!riderId) {
			return;
		}

		setShowModal(true);
		setIsLoading(true);
		setError("");

		try {
			const data = await getRiderHistory(riderId);
			setHistory(Array.isArray(data) ? data : []);
		} catch (fetchError) {
			setError("Unable to load rider history right now.");
		} finally {
			setIsLoading(false);
		}
	};

	const openAthleteProfile = () => {
		setShowModal(false);
		setShowProfileDrawer(true);
	};

	return (
		<>
			<Button
				variant="outline-info"
				size={size}
				className={className}
				onClick={openHistory}
				disabled={!riderId}
				aria-label={`View ${riderName} rider history`}
			>
				{!hideIcon && <FaHistory className={iconOnly ? "" : "me-1"} />}
				{iconOnly ? "" : "History"}
			</Button>

			<Modal
				show={showModal}
				onHide={() => setShowModal(false)}
				size="lg"
				centered
				data-bs-theme="dark"
			>
				<Modal.Header closeButton>
					<div className="d-flex justify-content-between align-items-center gap-2 w-100">
						<Modal.Title>
							{riderName} <Badge bg="secondary">Rider History</Badge>
						</Modal.Title>
						<Button
							variant="outline-primary"
							size="sm"
							onClick={openAthleteProfile}
							disabled={!riderId}
						>
							Athlete Profile
						</Button>
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex flex-wrap gap-2 mb-3">
						<Badge bg="secondary">{seasonHistory.length} events</Badge>
						<Badge bg="info" text="dark">
							{totalHistoryPoints} total points
						</Badge>
					</div>
					<div className="mb-3">
						<Form.Group>
							<Form.Label className="fw-semibold mb-1">Season</Form.Label>
							<Form.Select
								size="sm"
								value={selectedSeasonKey}
								onChange={(event) => setSelectedSeasonKey(event.target.value)}
								className="w-auto"
								disabled={seasonOptions.length === 0}
							>
								{seasonOptions.map((seasonOption) => (
									<option key={seasonOption.key} value={seasonOption.key}>
										{seasonOption.label}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</div>

					{isLoading && (
						<div className="d-flex justify-content-center py-4">
							<Spinner animation="border" role="status" />
						</div>
					)}

					{error && (
						<Alert variant="danger" className="mb-0">
							{error}
						</Alert>
					)}

					{!isLoading && !error && seasonHistory.length === 0 && (
						<Alert variant="info" className="mb-0">
							No available rider history for the selected season.
						</Alert>
					)}

					{!isLoading && !error && seasonHistory.length > 0 && (
						<Table
							responsive
							striped
							hover
							bordered
							className="align-middle mb-0"
							size="sm"
						>
							<thead>
								<tr>
									<th>Event</th>
									<th>Date</th>
									<th>Series</th>
									{showQualifyingColumn && <th>Quali</th>}
									{showSemisColumn && <th>Semis</th>}
									<th>Finals</th>
									<th>Total</th>
								</tr>
							</thead>
							<tbody>
								{seasonHistory.map((item) => (
									<tr key={item._id || `${item.event?.name}-${item.event_date}`}>
										<td>
											<div className="fw-semibold">{item.event?.name || "-"}</div>
											<div className="small text-muted">
												{item.event?.location || "-"}
												{item.event?.round ? ` • Round ${item.event.round}` : ""}
											</div>
										</td>
										<td>{formatDateValue(item.event_date || item.event?.date)}</td>
										<td>
											<div>{item.series?.name || "-"}</div>
											<div className="small text-muted">{item.sport?.name || "-"}</div>
										</td>
										{showQualifyingColumn && (
											<td>{formatPhaseValue(item.qualifying_data)}</td>
										)}
										{showSemisColumn && <td>{formatPhaseValue(item.semis_data)}</td>}
										<td>{formatPhaseValue(item.finals_data)}</td>
										<td className="fw-bold">{item.total_points ?? 0}</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</Modal.Body>
			</Modal>

			<AthleteProfileDrawer
				show={showProfileDrawer}
				onHide={() => setShowProfileDrawer(false)}
				riderId={riderId}
				riderName={riderName}
				initialRider={initialRider}
				initialHistory={history}
			/>
		</>
	);
};

export default RiderHistoryButton;
